import { API_BASE_URL } from '../config/api';
import { ensureAccessToken } from './accountsAuth';
import { classifyAuthFailure, handleAuthFailure } from './authFailure';
import { ApiCallConfig, ApiResult, ApiResponse } from '../types/api/common';

interface FetchOptions extends RequestInit {
  timeout?: number;
  headers?: Record<string, string>;
}

function createFetchWithTimeout(
  uri: string,
  options: FetchOptions,
  timeout: number,
  urlEncoded = false,
): Promise<Response> {
  return new Promise<Response>((resolve, reject) => {
    const encodedUri = urlEncoded ? uri : encodeURI(uri);
    let didTimeOut = false;

    const timer = setTimeout(() => {
      didTimeOut = true;
      reject(new Error('Request timed out'));
    }, timeout);

    fetch(encodedUri, options as RequestInit)
      .then((response) => {
        clearTimeout(timer);
        if (!didTimeOut) resolve(response);
      })
      .catch((err: Error) => {
        clearTimeout(timer);
        if (didTimeOut) return;
        if (import.meta.env.DEV) {
          console.error('Error:', err.message, encodedUri);
        }
        reject(err);
      });
  });
}

async function fetchWithTimeout(
  uri: string,
  options: FetchOptions = {},
  urlEncoded = false,
): Promise<Response> {
  const finalUrl = uri.startsWith('http') ? uri : API_BASE_URL + uri;

  // DEV-101: пока идёт переход, отправляем и Bearer, и прежний clientSessionId в URL.
  // Бэкенд принимает любой из двух, так что порядок выкладки фронта и включения флага
  // перестаёт иметь значение. Токена нет - заголовка нет, поведение прежнее.
  const accessToken = await ensureAccessToken();
  if (accessToken) {
    options.headers = { ...options.headers, Authorization: `Bearer ${accessToken}` };
  }

  if (!options.credentials) {
    options.credentials = 'include';
  }

  options.headers = {
    ...options.headers,
    // 'X-Feature-Flags': 'pkasko',
  };

  if (options.method?.toUpperCase() === 'POST') {
    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  return createFetchWithTimeout(finalUrl, options, options.timeout || 30000, urlEncoded);
}

export const apiCall =
  <T = any>({ url, method, urlEncoded = false }: ApiCallConfig) =>
  (data?: any): Promise<ApiResult<T>> =>
    fetchWithTimeout(url, {
      method,
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }, urlEncoded)
      .then(async (response) => {
        const json = (await response.json()) as ApiResponse<T>;

        // DEV-101 §24.3: отказ по доступу приводим к конверту, который вызывающий код
        // уже умеет читать (`res.error`). Без этого 401/403/503 доезжали до компонентов
        // как обычный ответ: список полисов просто оставался пустым, без объяснения.
        const failure = classifyAuthFailure(response.status, json);
        if (failure) {
          handleAuthFailure(failure);
          return [null, { data: null, error: true, message: failure.message }] as ApiResult<T>;
        }

        return [null, json] as ApiResult<T>;
      })
      .catch((error: Error) => {
        if (import.meta.env.DEV) {
          console.error('API Response Error:', url, error.message);
        }
        return [error, null] as ApiResult<T>;
      });

export { fetchWithTimeout };
